import { ErrorMessage, TaAutocomplete } from '@components/shared';
import { NotificationSnackbar } from '@components/shared/notificationSnackbar';
import { TaModal } from '@components/shared/TaModal/TaModal';
import { EditableCard } from '@features/dashboard/specialistDashboard/editableCard/editableCard';
import { Chip } from '@mui/material';
import { mapToDictionary } from '@utils/util.functions';
import { Dictionary } from '@utils/util.types';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCrops, useCropsStore } from 'services/crops';
import { Crop, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';
import crop from '../../../../assets/icons/crop.svg';

const offerValidationSchema = Yup.object().shape({
	crops: Yup.array().min(1, 'fieldNameError'),
	service: Yup.array().min(1, 'fieldNameError'),
	specialties: Yup.array().min(1, 'fieldNameError'),
});

const initialValues = {
	crops: [],
};

interface OfferFormValues {
	crops: Dictionary[];
}

interface OfferProps {
	readonly?: boolean;
}

export const FarmerOffer = ({ readonly = false }: OfferProps) => {
	const { t } = useTranslation();
	const { crops } = useCropsStore();
	const mutate = useUpdateUser();
	const { user } = useUsersStore();
	const [isLoading, setIsLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);

	useCrops();

	const cropsDictionary = useMemo(() => mapToDictionary(crops), [crops]);

	const formik = useFormik<OfferFormValues>({
		initialValues: initialValues,
		validationSchema: offerValidationSchema,
		onSubmit: async (values) => {
			const updatePayload = {
				crops: values.crops.map((crop) => crop.id),
			};
			setIsLoading(true);
			try {
				await mutate(updatePayload);
				setOpen(false);
				setSnackbarOpen(true);
				setIsLoading(false);
			} catch (error) {
				console.error('Update failed:', error);
				setIsLoading(false);
			}
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		if (user) {
			setValues({
				crops: mapToDictionary(user.user_crops),
			});
		}
	}, [setValues, user, user?.user_crops]);

	return (
		<EditableCard
			className="responsive-farmer-card"
			title={t('cropsGrow')}
			onEdit={() => setOpen(true)}
			readonly={readonly}
		>
			<div className="flex flex-col gap-[20px] pb-5">
				<div className="flex flex-wrap gap-[10px]">
					<img className="text-secondary" src={crop} alt="crop" />
					<div className="w-[1px] h-[20px] bg-[#E9ECEF]"></div>
					<p className="text-[#343A40] roboto-regular text-[16px] leading-[140%]">{t('iGrow')}</p>
					{user?.user_crops.map((item: Crop) => (
						<Chip
							key={item.id}
							label={item.name}
							variant="outlined"
							sx={{
								borderRadius: '8px',
								border: '1px solid #CED4DA',
								backgroundColor: '#F8F9FA',
								padding: '1px 10px',
								color: '#7B809A',
								fontFamily: 'Roboto',
								fontSize: '16px',
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: '140%',
								height: '24px',
								textTransform: 'camelcase',
								'& .MuiChip-label': {
									padding: 0,
								},
							}}
						/>
					))}
				</div>
			</div>
			<TaModal
				save={formik.handleSubmit}
				open={open}
				saveButtonLoading={isLoading}
				disableSaveButton={isLoading}
				onClose={() => setOpen(false)}
				title={t('selectCrops')}
				showActions
			>
				<div className="flex flex-col gap-[20px] pb-5 mt-[20px]">
					<div className="flex flex-col gap-[10px]">
						<span className="roboto-regular text-[16px] leading-[140%]">{t('cropsIGrow')}</span>
						<div>
							<TaAutocomplete
								options={
									formik.values.crops
										? cropsDictionary.filter(
												(option) =>
													!formik.values.crops.some((selected) => selected.id === option.id)
											)
										: cropsDictionary
								}
								getOptionLabel={(option) => option.name}
								label={t('crops')}
								icon={
									<img
										className={
											formik.touched.crops && formik.errors.crops ? 'error' : 'text-secondary'
										}
										src={crop}
										alt="crop"
									/>
								}
								multiple
								filterSelectedOptions
								value={formik.values.crops}
								onChange={(event, value) => formik.setFieldValue('crops', value)}
								error={Boolean(formik.touched.crops && formik.errors.crops)}
							/>
							{formik.touched.crops && formik.errors.crops && (
								<ErrorMessage
									message={t(formik.errors.crops as string, { fieldName: t('crops') })}
								/>
							)}
						</div>
					</div>
				</div>
			</TaModal>
			<NotificationSnackbar
				open={snackbarOpen}
				onClose={() => setSnackbarOpen(false)}
				title={t('successfullyUpdated')}
				success
				description={t('profileUpdated', {
					title: t('crops'),
				})}
			/>
		</EditableCard>
	);
};
